<template>
  <div class="pd-home">
    <!-- Full-Width Hero Carousel -->
    <section class="pd-hero-carousel">
      <div class="pd-carousel-container">
        <div class="pd-carousel-slide" :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
          <!-- Dynamic Banners from Supabase -->
          <div
            v-for="banner in filteredBanners"
            :key="banner.id"
            class="pd-slide"
          >
            <img
              :src="getBannerImage(banner)"
              :alt="banner.title || 'Point of Demon Banner'"
            />
          </div>
        </div>

        <!-- Carousel Controls -->
        <button class="pd-carousel-prev" @click="prevSlide">
          <i class="fas fa-chevron-left"></i>
        </button>
        <button class="pd-carousel-next" @click="nextSlide">
          <i class="fas fa-chevron-right"></i>
        </button>

        <!-- Carousel Indicators -->
        <div class="pd-carousel-indicators">
          <button
            v-for="(banner, index) in filteredBanners"
            :key="index"
            :class="{ active: currentSlide === index }"
            @click="goToSlide(index)"
          ></button>
        </div>
      </div>
    </section>

    <!-- Featured Images Section (Model Photos) - Carousel -->
    <section v-if="filteredFeaturedImages.length > 0" class="pd-featured-carousel">
      <div class="pd-featured-carousel-container">
        <div class="pd-featured-carousel-wrapper">
          <div class="pd-featured-carousel-slide" :style="{ transform: `translateX(-${currentFeaturedSlide * 100}%)` }">
            <div
              v-for="image in filteredFeaturedImages"
              :key="image.id"
              class="pd-featured-slide"
            >
              <img
                :src="getFeaturedImage(image)"
                :alt="image.alt_text"
                class="pd-featured-image"
              />
            </div>
          </div>

          <!-- Carousel Controls (only show if more than 1 image) -->
          <template v-if="filteredFeaturedImages.length > 1">
            <button class="pd-featured-prev" @click="prevFeaturedSlide">
              <i class="fas fa-chevron-left"></i>
            </button>
            <button class="pd-featured-next" @click="nextFeaturedSlide">
              <i class="fas fa-chevron-right"></i>
            </button>

            <!-- Carousel Indicators -->
            <div class="pd-featured-indicators">
              <button
                v-for="(image, index) in filteredFeaturedImages"
                :key="index"
                :class="{ active: currentFeaturedSlide === index }"
                @click="goToFeaturedSlide(index)"
              ></button>
            </div>
          </template>
        </div>
      </div>
    </section>

    <!-- Product Grid Section -->
    <section class="pd-section pd-products-section">
      <div class="pd-container">
        <h2 class="pd-section-title">新品上市</h2>

        <EcSpinner v-if="isLoading" />

        <div v-else class="pd-products-grid-home">
          <ProductsCard
            v-for="product in topProducts"
            :key="product.id"
            :product="product"
          />
        </div>

        <div class="pd-view-all-center">
          <router-link to="/products" class="pd-btn-view-all">
            查看全部商品
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { Toast } from '@/utils/helpers'
import productsAPI from '@/apis/products'
import ProductsCard from '@/components/ProductsCard'
import EcSpinner from '@/components/EcSpinner'

export default {
  name: 'EcHome',
  data () {
    return {
      products: [],
      banners: [
        { id: 1, image_url: 'banner-black.jpg', title: null },
        { id: 2, image_url: 'banner-white.jpg', title: null }
      ],
      featuredImages: [],
      isLoading: true,
      currentSlide: 0,
      currentFeaturedSlide: 0,
      autoPlayInterval: null,
      featuredAutoPlayInterval: null,
      isMobile: false,
      allBanners: [],
      allFeaturedImages: []
    }
  },
  computed: {
    topProducts () {
      // Show top 15 products
      return this.products.slice(0, 15)
    },
    // 根据设备过滤 Banners：桌面版只显示有 image_url 的，手机版只显示有 mobile_image_url 的
    filteredBanners () {
      if (!this.allBanners || this.allBanners.length === 0) {
        return this.banners // 返回默认 banners
      }
      if (this.isMobile) {
        // 手机版：只显示有 mobile_image_url 的 Banner，按 mobile_display_order 排序
        return this.allBanners
          .filter(banner => banner.mobile_image_url)
          .sort((a, b) => (a.mobile_display_order || 0) - (b.mobile_display_order || 0))
      } else {
        // 桌面版：只显示有 image_url 的 Banner，按 display_order 排序
        return this.allBanners
          .filter(banner => banner.image_url)
          .sort((a, b) => a.display_order - b.display_order)
      }
    },
    // 根据设备过滤 Featured Images
    filteredFeaturedImages () {
      if (!this.allFeaturedImages || this.allFeaturedImages.length === 0) {
        return []
      }
      if (this.isMobile) {
        return this.allFeaturedImages
          .filter(image => image.mobile_image_url)
          .sort((a, b) => (a.mobile_display_order || 0) - (b.mobile_display_order || 0))
      } else {
        return this.allFeaturedImages
          .filter(image => image.image_url)
          .sort((a, b) => a.display_order - b.display_order)
      }
    }
  },
  watch: {
    // 当设备类型改变时，重置轮播位置
    isMobile () {
      this.currentSlide = 0
      this.currentFeaturedSlide = 0
    }
  },
  created () {
    this.fetchProducts()
    this.fetchBanners()
    this.fetchFeaturedImage()
  },
  mounted () {
    this.startAutoPlay()
    this.startFeaturedAutoPlay()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeDestroy () {
    this.stopAutoPlay()
    this.stopFeaturedAutoPlay()
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    async fetchProducts () {
      console.log('Home.vue: fetchProducts called.')
      try {
        const response = await productsAPI.getProducts()
        console.log('Home.vue: productsAPI.getProducts response:', response)

        if (response.statusText !== 'OK') {
          console.error('Home.vue: productsAPI.getProducts response statusText not OK:', response.statusText)
          throw new Error(response.statusText)
        }
        console.log('Home.vue: raw products data:', response.data.products)
        this.products = response.data.products || []
        this.isLoading = false
        console.log('Home.vue: Products loaded successfully.', this.products.length, 'products.')
      } catch (error) {
        console.error('Home.vue: Error fetching products:', error)
        Toast.fire({
          type: 'error',
          title: '無法取得商品資料'
        })
        this.isLoading = false
      }
    },
    async fetchBanners () {
      try {
        const { supabase } = await import('@/supabase')
        const { data, error } = await supabase
          .from('banners')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true })

        if (error) {
          console.error('Supabase error:', error)
          // 保持默认 banners（在 data 中定义）
          this.allBanners = []
        } else {
          this.allBanners = data || []
        }
      } catch (error) {
        console.error('Error fetching banners:', error)
        this.allBanners = []
      }
    },
    async fetchFeaturedImage () {
      try {
        const { supabase } = await import('@/supabase')
        const { data, error } = await supabase
          .from('featured_images')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true })

        if (error) {
          console.error('Supabase error fetching featured images:', error)
          return
        }

        this.allFeaturedImages = data || []
      } catch (error) {
        console.error('Error fetching featured images:', error)
      }
    },
    nextSlide () {
      this.currentSlide = (this.currentSlide + 1) % this.filteredBanners.length
    },
    prevSlide () {
      this.currentSlide = (this.currentSlide - 1 + this.filteredBanners.length) % this.filteredBanners.length
    },
    goToSlide (index) {
      this.currentSlide = index
    },
    startAutoPlay () {
      this.autoPlayInterval = setInterval(() => {
        this.nextSlide()
      }, 5000) // Auto-play every 5 seconds
    },
    stopAutoPlay () {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval)
      }
    },
    // Featured Images Carousel Methods
    nextFeaturedSlide () {
      this.currentFeaturedSlide = (this.currentFeaturedSlide + 1) % this.filteredFeaturedImages.length
    },
    prevFeaturedSlide () {
      this.currentFeaturedSlide = (this.currentFeaturedSlide - 1 + this.filteredFeaturedImages.length) % this.filteredFeaturedImages.length
    },
    goToFeaturedSlide (index) {
      this.currentFeaturedSlide = index
    },
    startFeaturedAutoPlay () {
      // Only start auto-play if there are featured images
      this.featuredAutoPlayInterval = setInterval(() => {
        if (this.filteredFeaturedImages.length > 1) {
          this.nextFeaturedSlide()
        }
      }, 5000) // Auto-play every 5 seconds
    },
    stopFeaturedAutoPlay () {
      if (this.featuredAutoPlayInterval) {
        clearInterval(this.featuredAutoPlayInterval)
      }
    },
    // 检测是否为手机设备
    checkMobile () {
      this.isMobile = window.innerWidth <= 768
    },
    // 根据设备返回对应的 Banner 图片（已由 filteredBanners 过滤，不需要 fallback）
    getBannerImage (banner) {
      if (this.isMobile) {
        return banner.mobile_image_url
      }
      return banner.image_url
    },
    // 根据设备返回对应的 Featured Image（已由 filteredFeaturedImages 过滤，不需要 fallback）
    getFeaturedImage (image) {
      if (this.isMobile) {
        return image.mobile_image_url
      }
      return image.image_url
    }
  },
  components: {
    ProductsCard,
    EcSpinner
  }
}
</script>

<style scoped>
.pd-home {
  background-color: var(--pd-white);
}

/* Hero Carousel */
.pd-hero-carousel {
  position: relative;
  width: 100%;
  height: 70vh;
  min-height: 500px;
  overflow: hidden;
  background-color: var(--pd-black);
}

.pd-carousel-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.pd-carousel-slide {
  display: flex;
  height: 100%;
  transition: transform 0.6s ease;
}

.pd-slide {
  position: relative;
  min-width: 100%;
  height: 100%;
}

.pd-slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Logo Slides - Center the logo */
.pd-slide-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--pd-black);
}

.pd-slide-logo img {
  width: auto;
  height: auto;
  max-width: 60%;
  max-height: 60%;
  object-fit: contain;
}

.pd-slide-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6));
  display: flex;
  align-items: center;
  justify-content: center;
}

.pd-slide-content {
  text-align: center;
  color: var(--pd-white);
  z-index: 2;
}

.pd-slide-title {
  font-size: 3.5rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  margin-bottom: 1rem;
  text-transform: uppercase;
}

.pd-slide-subtitle {
  font-size: 1.5rem;
  font-weight: 300;
  letter-spacing: 0.2em;
}

/* Carousel Controls */
.pd-carousel-prev,
.pd-carousel-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: var(--pd-white);
  font-size: 1.5rem;
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: all var(--transition-normal);
  z-index: 10;
}

.pd-carousel-prev:hover,
.pd-carousel-next:hover {
  background: rgba(255, 255, 255, 0.4);
}

.pd-carousel-prev {
  left: 2rem;
}

.pd-carousel-next {
  right: 2rem;
}

/* Carousel Indicators */
.pd-carousel-indicators {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

.pd-carousel-indicators button {
  width: 8px;
  height: 8px;
  min-width: 8px !important; /* Override global 44px minimum */
  min-height: 8px !important;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.pd-carousel-indicators button.active {
  background: rgba(255, 255, 255, 0.9);
  width: 24px;
  border-radius: 12px;
}

.pd-carousel-indicators button:hover {
  background: rgba(255, 255, 255, 0.6);
}

/* Featured Images Carousel (Model Photos) */
/* Fixed size: 1110x555 */
.pd-featured-carousel {
  position: relative;
  width: 100%;
  padding: 4rem 0;
  background-color: var(--pd-white);
}

.pd-featured-carousel-container {
  position: relative;
  max-width: 1110px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
}

.pd-featured-carousel-container::before {
  content: '';
  display: block;
  padding-top: 50%; /* 2:1 Aspect Ratio (555/1110 = 0.5 = 50%) */
}

.pd-featured-carousel-wrapper {
  position: absolute;
  top: 0;
  left: var(--spacing-md);
  right: var(--spacing-md);
  bottom: 0;
  overflow: hidden;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.pd-featured-carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  transition: transform 0.6s ease;
}

.pd-featured-slide {
  position: relative;
  min-width: 100%;
  height: 100%;
  flex-shrink: 0;
}

.pd-featured-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Featured Carousel Controls */
.pd-featured-prev,
.pd-featured-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.4);
  border: none;
  color: var(--pd-white);
  font-size: 1.5rem;
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: all var(--transition-normal);
  z-index: 10;
}

.pd-featured-prev:hover,
.pd-featured-next:hover {
  background: rgba(0, 0, 0, 0.6);
}

.pd-featured-prev {
  left: 2rem;
}

.pd-featured-next {
  right: 2rem;
}

/* Featured Carousel Indicators */
.pd-featured-indicators {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

.pd-featured-indicators button {
  width: 8px;
  height: 8px;
  min-width: 8px !important;
  min-height: 8px !important;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.6);
  background: transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.pd-featured-indicators button.active {
  background: rgba(0, 0, 0, 0.8);
  width: 24px;
  border-radius: 12px;
}

.pd-featured-indicators button:hover {
  background: rgba(0, 0, 0, 0.5);
}

/* Products Section */
.pd-products-section {
  padding: 5rem 0;
}

.pd-section-title {
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  margin-bottom: 3rem;
  color: var(--pd-black);
}

.pd-products-grid-home {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 3rem;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.pd-view-all-center {
  text-align: center;
  margin-top: 3rem;
}

.pd-btn-view-all {
  display: inline-block;
  padding: 1rem 3rem;
  background-color: var(--pd-black);
  color: var(--pd-white);
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  transition: all var(--transition-normal);
  border: 2px solid var(--pd-black);
}

.pd-btn-view-all:hover {
  background-color: var(--pd-white);
  color: var(--pd-black);
  opacity: 1;
}

/* Responsive */
@media (max-width: 1024px) {
  .pd-products-grid-home {
    grid-template-columns: repeat(3, 1fr);
  }

  .pd-slide-title {
    font-size: 2.5rem;
  }

  .pd-slide-subtitle {
    font-size: 1.125rem;
  }
}

@media (max-width: 768px) {
  .pd-hero-carousel {
    height: auto;
    min-height: unset;
  }

  .pd-slide {
    position: relative;
    width: 100%;
    padding-top: 133.33%; /* 3:4 Aspect Ratio (750x1000) for mobile */
  }

  .pd-slide img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .pd-featured-carousel {
    padding: 3rem 0;
  }

  .pd-featured-carousel-container {
    padding: 0 var(--spacing-sm);
  }

  .pd-featured-carousel-container::before {
    padding-top: 100%; /* 1:1 Aspect Ratio (750x750 Square) for mobile */
  }

  .pd-featured-carousel-wrapper {
    left: var(--spacing-sm);
    right: var(--spacing-sm);
  }

  .pd-products-grid-home {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .pd-slide-title {
    font-size: 2rem;
  }

  .pd-slide-subtitle {
    font-size: 1rem;
  }

  .pd-carousel-prev,
  .pd-carousel-next,
  .pd-featured-prev,
  .pd-featured-next {
    padding: 0.75rem 1rem;
    font-size: 1.25rem;
  }

  .pd-carousel-prev,
  .pd-featured-prev {
    left: 1rem;
  }

  .pd-carousel-next,
  .pd-featured-next {
    right: 1rem;
  }
}

@media (max-width: 480px) {
  .pd-products-grid-home {
    grid-template-columns: repeat(2, 1fr);
  }

  .pd-section-title {
    font-size: 1.5rem;
  }

  .pd-featured-carousel {
    padding: 2rem 0;
  }

  .pd-featured-carousel-wrapper {
    left: var(--spacing-xs);
    right: var(--spacing-xs);
  }

  .pd-carousel-prev,
  .pd-carousel-next,
  .pd-featured-prev,
  .pd-featured-next {
    padding: 0.5rem 0.75rem;
    font-size: 1rem;
  }

  .pd-carousel-prev,
  .pd-featured-prev {
    left: 0.5rem;
  }

  .pd-carousel-next,
  .pd-featured-next {
    right: 0.5rem;
  }
}
</style>
